/**
 * Season Standings — leaderboard for the current active season.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ArrowLeft, Trophy, Building2 } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { TYPO } from '@/constants/typography';
import {
  getActiveSeason,
  getSeasonStandings,
  getSeasonParticipantCount,
  getGymSeasonStandings,
  type Season,
  type SeasonStanding,
} from '@/lib/seasons';

export default function SeasonStandingsScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { session, profile } = useAuth();
  const { tier } = useSubscription();
  const isClub = hasAtLeast(tier, 'org_pro');
  const userId = session?.user?.id;
  const gymId = profile?.gym_id;

  const [season, setSeason] = useState<Season | null>(null);
  const [standings, setStandings] = useState<SeasonStanding[]>([]);
  const [participantCount, setParticipantCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'global' | 'gym'>('global');

  const load = useCallback(async () => {
    const s = await getActiveSeason();
    setSeason(s);
    if (!s) { setLoading(false); return; }

    if (viewMode === 'gym' && isClub && gymId) {
      const st = await getGymSeasonStandings(s.id, gymId);
      setStandings(st);
      setParticipantCount(st.length);
    } else {
      const [st, count] = await Promise.all([
        getSeasonStandings(s.id),
        getSeasonParticipantCount(s.id),
      ]);
      setStandings(st);
      setParticipantCount(count);
    }
    setLoading(false);
  }, [viewMode, isClub, gymId]);

  useEffect(() => { load(); }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  const currentUser = standings.find(s => s.user_id === userId);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const getInitial = (s: SeasonStanding) =>
    (s.display_name?.[0] ?? s.handle?.[0] ?? '?').toUpperCase();

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
          <ArrowLeft size={24} color={c.text} />
        </TouchableOpacity>
        <Trophy size={20} color={c.accent} style={{ marginLeft: 12 }} />
        <Text style={[styles.headerTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>
          STANDINGS
        </Text>
      </View>

      {/* Club: Global / My Gym toggle */}
      {isClub && gymId && (
        <View style={{ flexDirection: 'row', marginHorizontal: 16, marginBottom: 8, borderWidth: 1, borderColor: c.border, borderRadius: 2 }}>
          <TouchableOpacity
            style={{ flex: 1, paddingVertical: 8, alignItems: 'center', backgroundColor: viewMode === 'global' ? c.accent : 'transparent' }}
            onPress={() => setViewMode('global')}
          >
            <Text style={{ color: viewMode === 'global' ? '#000' : c.textSecondary, fontFamily: TYPO.body.semibold, fontSize: 13 }}>Global</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={{ flex: 1, paddingVertical: 8, alignItems: 'center', flexDirection: 'row', justifyContent: 'center', gap: 4, backgroundColor: viewMode === 'gym' ? c.accent : 'transparent' }}
            onPress={() => setViewMode('gym')}
          >
            <Building2 size={13} color={viewMode === 'gym' ? '#000' : c.textSecondary} />
            <Text style={{ color: viewMode === 'gym' ? '#000' : c.textSecondary, fontFamily: TYPO.body.semibold, fontSize: 13 }}>My Gym</Text>
          </TouchableOpacity>
        </View>
      )}

      {season && (
        <View style={styles.seasonInfo}>
          <Text style={[styles.seasonName, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
            {season.name.toUpperCase()}
          </Text>
          <Text style={[styles.seasonDates, { color: c.textSecondary }]}>
            {formatDate(season.starts_at)} – {formatDate(season.ends_at)}
          </Text>
          <Text style={[styles.participantCount, { color: c.textTertiary }]}>
            {participantCount} competitor{participantCount !== 1 ? 's' : ''}
          </Text>
        </View>
      )}

      <ScrollView
        style={styles.list}
        contentContainerStyle={standings.length === 0 ? styles.emptyContainer : undefined}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />
        }
      >
        {!loading && standings.length === 0 && (
          <View style={styles.emptyState}>
            <Trophy size={48} color={c.textTertiary} />
            <Text style={[styles.emptyText, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
              NO PARTICIPANTS YET
            </Text>
            <Text style={[styles.emptySubtext, { color: c.textTertiary }]}>
              Join the season to be the first on the board.
            </Text>
          </View>
        )}

        {standings.map((s) => {
          const isMe = s.user_id === userId;
          return (
            <View
              key={s.user_id}
              style={[
                styles.row,
                { borderBottomColor: c.border },
                isMe && { backgroundColor: c.accent + '10' },
              ]}
            >
              <Text style={[styles.rank, { color: s.rank <= 3 ? c.accent : c.textSecondary, fontFamily: TYPO.heading.bold }]}>
                #{s.rank}
              </Text>
              <View style={[styles.avatar, { backgroundColor: c.accent + '20' }]}>
                <Text style={[styles.avatarText, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
                  {getInitial(s)}
                </Text>
              </View>
              <View style={styles.nameCol}>
                <Text style={[styles.displayName, { color: c.text, fontFamily: TYPO.body.semibold }]} numberOfLines={1}>
                  {s.display_name ?? s.handle ?? 'Unknown'}
                </Text>
                {s.handle && (
                  <Text style={[styles.handle, { color: c.textTertiary }]} numberOfLines={1}>
                    @{s.handle}
                  </Text>
                )}
              </View>
              <Text style={[styles.score, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                {s.strength_score != null ? Math.round(s.strength_score) : '—'}
              </Text>
            </View>
          );
        })}
      </ScrollView>

      {/* Pinned current user */}
      {currentUser && (
        <View style={[styles.pinnedRow, { backgroundColor: c.surface, borderTopColor: c.accent + '40' }]}>
          <Text style={[styles.rank, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
            #{currentUser.rank}
          </Text>
          <View style={[styles.avatar, { backgroundColor: c.accent + '30' }]}>
            <Text style={[styles.avatarText, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
              {getInitial(currentUser)}
            </Text>
          </View>
          <View style={styles.nameCol}>
            <Text style={[styles.displayName, { color: c.text, fontFamily: TYPO.body.semibold }]} numberOfLines={1}>
              {currentUser.display_name ?? currentUser.handle ?? 'You'}
            </Text>
            <Text style={[styles.handle, { color: c.textTertiary }]}>Your rank</Text>
          </View>
          <Text style={[styles.score, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
            {currentUser.strength_score != null ? Math.round(currentUser.strength_score) : '—'}
          </Text>
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerTitle: {
    fontSize: 18,
    letterSpacing: 1,
    marginLeft: 8,
  },
  seasonInfo: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  seasonName: {
    fontSize: 15,
    letterSpacing: 1,
    marginBottom: 2,
  },
  seasonDates: { fontSize: 12, marginBottom: 2 },
  participantCount: { fontSize: 12 },
  list: { flex: 1 },
  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyState: { alignItems: 'center', gap: 8 },
  emptyText: { fontSize: 16, letterSpacing: 1, marginTop: 8 },
  emptySubtext: { fontSize: 13 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rank: { width: 40, fontSize: 15 },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
  },
  avatarText: { fontSize: 15 },
  nameCol: { flex: 1 },
  displayName: { fontSize: 14 },
  handle: { fontSize: 12, marginTop: 1 },
  score: { fontSize: 16, minWidth: 44, textAlign: 'right' },
  pinnedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopWidth: 1,
  },
});
